import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";

import { fetchApiData } from "../../store/slicers/apiSlicer.js";
import AppLayout from "../layouts/AppLayout.jsx";
import useChangeLanguage from "../../logic/useChangeLanguage.js";

const ContentSection = () => {
  const { _t, getArticleTitle, getArticleBody } = useChangeLanguage();
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);

  const {
    data: articleData,
    isLoading: articleLoading,
    error: articleError,
  } = useSelector((state) => ({
    data: state.apiData?.data?.article,
    isLoading: state.apiData?.loading?.article,
    error: state.apiData?.error?.article,
  }));

  const { data: relatedData, isLoading: relatedLoading } = useSelector(
    (state) => ({
      data: state.apiData?.data?.relatedArticles,
      isLoading: state.apiData?.loading?.relatedArticles,
    })
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    getArticleById();
  }, [id]);

  useEffect(() => {
    if (articleData) {
      setArticle(articleData);
      getRelatedArticles();
    }
  }, [articleData]);

  useEffect(() => {
    if (relatedData?.data) {
      const filtered = relatedData.data
        .filter((a) => a.id !== parseInt(id))
        .slice(0, 3);
      setRelatedArticles(filtered);
    }
  }, [relatedData, id]);

  const getArticleById = () => {
    dispatch(
      fetchApiData({
        url: `articles/${id}`,
        itemKey: "article",
      })
    );
  };

  const getRelatedArticles = () => {
    dispatch(
      fetchApiData({
        url: "articles",
        itemKey: "relatedArticles",
      })
    );
  };

  const stripHtml = (html) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleRelatedClick = (relatedId) => {
    history.push(`/detail_new/${relatedId}`);
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = article?.title || "";

    let shareUrl = "";
    switch (platform) {
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          url
        )}`;
        break;
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
          url
        )}&text=${encodeURIComponent(title)}`;
        break;
      case "linkedin":
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          url
        )}`;
        break;
      case "email":
        shareUrl = `mailto:?subject=${encodeURIComponent(
          title
        )}&body=${encodeURIComponent(url)}`;
        break;
      default:
        return;
    }

    if (platform === "email") {
      window.location.href = shareUrl;
    } else {
      window.open(shareUrl, "_blank", "width=600,height=400");
    }
  };

  if (articleLoading) {
    return (
      <div className="detail-news-screen">
        <div className="loading-container">
          <i
            className="pi pi-spin pi-spinner"
            style={{ fontSize: "3rem", color: "#00a652" }}
          ></i>
          <p>Chargement de l'article...</p>
        </div>
      </div>
    );
  }

  if (articleError || !article) {
    return (
      <div className="detail-news-screen">
        <div className="error-container">
          <i
            className="pi pi-exclamation-triangle"
            style={{ fontSize: "3rem", color: "#d32f2f" }}
          ></i>
          <h2>Article introuvable</h2>
          <p>Désolé, cet article n'existe pas ou a été supprimé.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="detail-news-screen">
      <article className="detail-content">
        <div className="detail-meta">
          <span className="detail-date">
            <i className="pi pi-calendar"></i> {formatDate(article.created_at)}
          </span>
          <span className="detail-author">
            <i className="pi pi-user"></i> ONPR
          </span>
        </div>

        <h1 className="detail-title"> {getArticleTitle(article)}</h1>

        {article.image_source_url && (
          <div className="detail-image-wrapper">
            <img
              src={article.image_source_url}
              alt={article.image_alt || article.title}
              className="detail-image"
            />
            {article.image_caption && (
              <p className="image-caption">{article.image_caption}</p>
            )}
          </div>
        )}

        <div
          className="detail-body"
          dangerouslySetInnerHTML={{ __html: getArticleBody(article) }}
        ></div>

        <div className="detail-footer">
          <div className="share-section">
            <h4> {_t("share")} </h4>
            <div className="share-buttons">
              <a
                href="#"
                className="share-btn facebook"
                onClick={(e) => {
                  e.preventDefault();
                  handleShare("facebook");
                }}
              >
                <i className="pi pi-facebook"></i>
              </a>
              <a
                href="#"
                className="share-btn twitter"
                onClick={(e) => {
                  e.preventDefault();
                  handleShare("twitter");
                }}
              >
                <i className="pi pi-twitter"></i>
              </a>
              <a
                href="#"
                className="share-btn linkedin"
                onClick={(e) => {
                  e.preventDefault();
                  handleShare("linkedin");
                }}
              >
                <i className="pi pi-linkedin"></i>
              </a>
              <a
                href="#"
                className="share-btn email"
                onClick={(e) => {
                  e.preventDefault();
                  handleShare("email");
                }}
              >
                <i className="pi pi-envelope"></i>
              </a>
            </div>
          </div>
        </div>
      </article>

      {relatedLoading && (
        <div className="related-loading">
          <i className="pi pi-spin pi-spinner"></i> Chargement des articles
          connexes...
        </div>
      )}

      {!relatedLoading && relatedArticles.length > 0 && (
        <aside className="related-articles">
          <h3>
            <i className="pi pi-list"></i> Articles connexes
          </h3>
          <div className="related-grid">
            {relatedArticles.map((related) => (
              <div
                key={related.id}
                className="related-card"
                onClick={() => handleRelatedClick(related.id)}
              >
                <div
                  className="related-image"
                  style={
                    related.image_source_url
                      ? { backgroundImage: `url(${related.image_source_url})` }
                      : {}
                  }
                >
                  {!related.image_source_url && "Article ONPR"}
                </div>
                <div className="related-content">
                  <h4>{getArticleTitle(related)}</h4>
                  <span className="related-date">
                    {new Date(related.created_at).toLocaleDateString("fr-FR")}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </aside>
      )}

      <style jsx>{`
        .detail-news-screen {
          max-width: 1200px;
          margin: 30px auto;
          padding: 0 20px;
        }
        .loading-container,
        .error-container {
          text-align: center;
          padding: 100px 20px;
        }
        .loading-container p,
        .error-container h2 {
          margin-top: 20px;
          color: #00a652;
        }
        .error-container p {
          color: #666;
          margin-bottom: 30px;
        }
        .detail-header {
          margin-bottom: 30px;
        }
        .back-btn {
          background: #00a652;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 5px;
          cursor: pointer;
          font-size: 1rem;
          transition: all 0.3s;
          display: inline-flex;
          align-items: center;
          gap: 10px;
        }
        .back-btn:hover {
          background: #d32f2f;
          transform: translateX(-5px);
        }
        .detail-content {
          background: white;
          border-radius: 15px;
          padding: 40px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          margin-bottom: 40px;
        }
        .detail-meta {
          display: flex;
          gap: 30px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }
        .detail-meta span {
          color: #666;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .detail-date,
        .detail-author {
          color: #d32f2f;
          font-weight: 600;
        }
        .detail-title {
          color: #00a652;
          font-size: 2.5rem;
          margin-bottom: 30px;
          line-height: 1.3;
        }
        .detail-image-wrapper {
          margin-bottom: 30px;
        }
        .detail-image {
          width: 100%;
          max-height: 500px;
          object-fit: cover;
          border-radius: 10px;
        }
        .image-caption {
          text-align: center;
          font-style: italic;
          color: #666;
          margin-top: 10px;
        }
        .detail-body {
          color: #333;
          font-size: 1.1rem;
          line-height: 1.8;
        }
        .detail-body p {
          margin-bottom: 20px;
        }
        .detail-body h1,
        .detail-body h2,
        .detail-body h3 {
          color: #00a652;
          margin-top: 30px;
          margin-bottom: 15px;
        }
        .detail-footer {
          margin-top: 40px;
          padding-top: 30px;
          border-top: 2px solid #e0e0e0;
        }
        .share-section h4 {
          color: #00a652;
          margin-bottom: 15px;
        }
        .share-buttons {
          display: flex;
          gap: 15px;
        }
        .share-btn {
          width: 45px;
          height: 45px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          text-decoration: none;
          transition: all 0.3s;
        }
        .share-btn.facebook {
          background: #1877f2;
        }
        .share-btn.twitter {
          background: #1da1f2;
        }
        .share-btn.linkedin {
          background: #0077b5;
        }
        .share-btn.email {
          background: #666;
        }
        .share-btn:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
        }
        .related-loading {
          text-align: center;
          padding: 20px;
          color: #00a652;
        }
        .related-articles {
          background: white;
          border-radius: 15px;
          padding: 30px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }
        .related-articles h3 {
          color: #00a652;
          margin-bottom: 25px;
          padding-bottom: 15px;
          border-bottom: 3px solid #d32f2f;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .related-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 20px;
        }
        .related-card {
          background: #f5f5f5;
          border-radius: 10px;
          overflow: hidden;
          cursor: pointer;
          transition: all 0.3s;
          border-top: 3px solid #00a652;
        }
        .related-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }
        .related-image {
          height: 150px;
          background: linear-gradient(135deg, #00a652 0%, #2d8659 100%);
          background-size: cover;
          background-position: center;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 0.85rem;
          padding: 15px;
          text-align: center;
        }
        .related-content {
          padding: 15px;
        }
        .related-content h4 {
          color: #00a652;
          font-size: 0.95rem;
          margin-bottom: 8px;
          line-height: 1.4;
        }
        .related-date {
          color: #d32f2f;
          font-size: 0.8rem;
          font-weight: 600;
        }
        @media (max-width: 768px) {
          .detail-title {
            font-size: 1.8rem;
          }
          .detail-content {
            padding: 20px;
          }
          .related-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};
const DetailNewsScreen = () => {
  return (
    <>
      <AppLayout>
        <ContentSection />
      </AppLayout>
    </>
  );
};

export default DetailNewsScreen;
